/**
 * Bindings Dialog
 *
 * Lists a login provider's feature bindings and lets an admin add/remove
 * them. Allowed on every provider, including `main-session` — only the
 * provider record itself is immutable (admin_router.py's `_reject_main_session`
 * guards only the provider PUT/DELETE routes, not the binding routes).
 */

import { useState, type ReactElement } from 'react';
import { useAdminFeatures } from '@features/devcontainer/hooks/useAdminFeatures';
import type { Provider } from '../../types';
import {
  useProviderBindings,
  useCreateBinding,
  useDeleteBinding,
} from '../../hooks/useAdminLoginProviders';

interface BindingsDialogProps {
  open: boolean;
  provider: Provider | null;
  onClose: () => void;
}

export function BindingsDialog({
  open,
  provider,
  onClose,
}: BindingsDialogProps): ReactElement | null {
  const providerId = open && provider ? provider.id : null;
  const { data: bindings, isLoading, error } = useProviderBindings(providerId);
  const { data: features } = useAdminFeatures(false);
  const createBinding = useCreateBinding();
  const deleteBinding = useDeleteBinding();

  const [selectedFeatureId, setSelectedFeatureId] = useState('');
  const [mutationError, setMutationError] = useState<string | null>(null);

  if (!open || !provider) return null;

  const boundFeatureIds = new Set((bindings ?? []).map((b) => b.feature_id));
  const availableFeatures = (features ?? []).filter((f) => !boundFeatureIds.has(f.id));

  const handleAdd = (): void => {
    if (!selectedFeatureId) return;
    setMutationError(null);
    createBinding.mutate(
      { providerId: provider.id, featureId: selectedFeatureId },
      {
        onSuccess: () => { setSelectedFeatureId(''); },
        onError: () => { setMutationError('Failed to add binding (it may already exist).'); },
      },
    );
  };

  const handleRemove = (bindingId: string): void => {
    setMutationError(null);
    deleteBinding.mutate(
      { providerId: provider.id, bindingId },
      { onError: () => { setMutationError('Failed to remove binding.'); } },
    );
  };

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label={`Feature bindings for ${provider.display_name}`}
      >
        <h2>Bindings — {provider.display_name}</h2>

        {isLoading ? (
          <div className="settings-loading">Loading bindings...</div>
        ) : error ? (
          <div className="dialog__error" role="alert">
            Failed to load bindings.
          </div>
        ) : (
          <ul className="bindings-dialog__list">
            {(bindings ?? []).length === 0 ? (
              <li className="bindings-dialog__empty">No features bound to this provider.</li>
            ) : (
              (bindings ?? []).map((binding) => (
                <li key={binding.id} className="bindings-dialog__row">
                  <span>{binding.feature_name}</span>
                  <button
                    type="button"
                    className="dialog__btn dialog__btn--secondary"
                    onClick={() => { handleRemove(binding.id); }}
                    disabled={deleteBinding.isPending}
                    aria-label={`Remove ${binding.feature_name} binding`}
                  >
                    Remove
                  </button>
                </li>
              ))
            )}
          </ul>
        )}

        <div className="bindings-dialog__add">
          <select
            value={selectedFeatureId}
            onChange={(e) => { setSelectedFeatureId(e.target.value); }}
            aria-label="Feature to bind"
          >
            <option value="">Select a feature…</option>
            {availableFeatures.map((f) => (
              <option key={f.id} value={f.id}>
                {f.display_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="dialog__btn dialog__btn--primary"
            onClick={handleAdd}
            disabled={!selectedFeatureId || createBinding.isPending}
          >
            {createBinding.isPending ? 'Adding...' : 'Add Binding'}
          </button>
        </div>

        {mutationError && (
          <div className="dialog__error" role="alert">
            {mutationError}
          </div>
        )}

        <div className="dialog__actions">
          <button type="button" onClick={onClose} className="dialog__btn dialog__btn--secondary">
            Close
          </button>
        </div>
      </dialog>
    </div>
  );
}
